import { logger } from "../services/logger.ts";
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Wallet, ArrowUpRight, ArrowDownRight, Clock, Search, X, Info, ArrowLeft } from 'lucide-react';
import { supabase } from "../lib/db.ts";
import { useAuth } from "../lib/auth.tsx";
import { useSettings } from "../lib/settings/settingsContext.tsx";


// Interface definitions following the patterns in wallet-system.md
interface AdminTransaction {
  id: string;
  user_id: string;
  type: 'deposit' | 'withdrawal' | 'trade' | 'refund' | 'bonus' | 'service_fee' | 'platform_liquidity_return' | 'adjustment' | 'payout';
  amount: number;
  balance_after: number;
  description: string | null;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  completed_at: string | null;
  related_entity_id?: string; // For tracking which bet/user this transaction relates to
  related_entity_type?: string; // What type of entity (bet, deposit, etc.)
}

interface AdminWallet {
  user_id: string;
  balance: number;
  created_at: string;
  updated_at: string;
}

// Add this constant at the top of the file, after the interfaces and before the component
const ADMIN_USER_ID = '00000000-0000-0000-0000-000000000000';

// Helper function to generate page numbers for pagination
const generatePageNumbers = (currentPage: number, totalPages: number, maxVisiblePages = 5): (number | string)[] => {
  if (totalPages <= maxVisiblePages) {
    // If total pages is small, show all page numbers
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];
  const halfVisible = Math.floor(maxVisiblePages / 2);
  
  // Always add the first page
  pages.push(1);

  let startPage = Math.max(2, currentPage - halfVisible);
  let endPage = Math.min(totalPages - 1, currentPage + halfVisible);

  // Adjust if near the start
  if (currentPage - halfVisible <= 2) {
      endPage = Math.min(totalPages - 1, maxVisiblePages - 1); // Account for first and last page
  }
  
  // Adjust if near the end
  if (currentPage + halfVisible >= totalPages - 1) {
      startPage = Math.max(2, totalPages - maxVisiblePages + 2); // Account for first and last page
  }

  // Add ellipsis before the main range if needed
  if (startPage > 2) {
    pages.push('...');
  }

  // Add the main range of pages
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  // Add ellipsis after the main range if needed
  if (endPage < totalPages - 1) {
    pages.push('...');
  }

  // Always add the last page
  pages.push(totalPages);

  return pages;
};

/** Display label for wallet transaction type column / modals (module scope for nested components). */
function getTransactionLabel(type: string, description: string | null | undefined): string {
  const desc = (description ?? '').toLowerCase();
  if (type === 'platform_liquidity_return') return 'Collateral return';
  if (type === 'adjustment' && /collateral|liquidity/i.test(desc)) return 'Collateral';
  if (type === 'payout' && /share market deposit return|collateral return|platform.*return/i.test(desc)) {
    return 'Collateral return';
  }
  if (type === 'service_fee' && /spread revenue/i.test(desc)) return 'Spread revenue';
  if (type === 'adjustment') return 'Adjustment';
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Sum portfolio-aligned fees: LMSR uses spread_revenue; pool uses service_fee_amount. */
async function sumPlatformFeeAndSpreadFromBets(): Promise<number> {
  const pageSize = 1000;
  let from = 0;
  let total = 0;
  for (;;) {
    const { data, error } = await supabase
      .from('user_bets')
      .select('spread_revenue, service_fee_amount, markets!inner(engine_type)')
      .order('created_at', { ascending: true })
      .range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data?.length) break;
    for (const row of data) {
      const m = row.markets as { engine_type?: string } | null;
      const engine = m?.engine_type;
      if (engine === 'lmsr') {
        total += Number(row.spread_revenue ?? 0);
      } else {
        total += Number(row.service_fee_amount ?? 0);
      }
    }
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return total;
}

function RelatedEntitySummary({ transaction }: { transaction: AdminTransaction }) {
  const id = transaction.related_entity_id?.trim();
  const kind =
    transaction.related_entity_type === 'bet'
      ? 'Trade'
      : transaction.related_entity_type === 'bet_payout'
        ? 'Payout'
        : transaction.related_entity_type === 'deposit'
          ? 'Deposit'
          : transaction.related_entity_type === 'withdrawal'
            ? 'Withdrawal'
            : 'Related';

  if (!id) {
    return <span className="text-sm text-gray-500">—</span>;
  }

  return (
    <div className="text-sm text-gray-900 flex items-center">
      {kind}
      <span>{` #${id.substring(0, 8)}...`}</span>
      <Info className="w-4 h-4 ml-1 text-gray-400" aria-hidden />
    </div>
  );
}

export function AdminWalletManagement() {
  const [wallet, setWallet] = useState<AdminWallet | null>(null);
  const [transactions, setTransactions] = useState<AdminTransaction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [transactionError, setTransactionError] = useState<string | null>(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [transactionLoading, setTransactionLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<AdminTransaction | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [totals, setTotals] = useState<{
    serviceFees: number;
    platformFeeAndSpread: number;
    deposits: number;
    withdrawals: number;
    totalTransactions: number;
  }>({
    serviceFees: 0,
    platformFeeAndSpread: 0,
    deposits: 0,
    withdrawals: 0,
    totalTransactions: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { formatCurrency } = useSettings();

  // Check if user is admin
  useEffect(() => {
    if (!user || user.app_metadata?.role !== 'admin') {
      navigate('/markets');
    }
  }, [user, navigate]);

  // Fetch wallet data with real-time subscription
  useEffect(() => {
    const fetchWalletData = async () => {
      if (!user) return;

      try {
        setWalletLoading(true);
        setWalletError(null);
        
        // Check if current user is admin directly from the user object
        if (!user || user.app_metadata?.role !== 'admin') {
          throw new Error('User is not an admin');
        }
        
        // Find the admin wallet
        const { data: existingWallets, error: fetchError } = await supabase
          .from('user_wallets')
          .select('*')
          .eq('user_id', ADMIN_USER_ID)
          .limit(1);
          
        if (fetchError) {
          logger.error('Error fetching admin wallet:', fetchError);
          throw fetchError;
        }
        
        // If no wallet exists, create one
        if (!existingWallets || existingWallets.length === 0) {
          const { data: newWallet, error: createError } = await supabase
            .from('user_wallets')
            .insert({ user_id: ADMIN_USER_ID, balance: 0 })
            .select()
            .single();
            
          if (createError) {
            logger.error('Error creating admin wallet:', createError);
            throw createError;
          }
          
          if (newWallet) {
            setWallet({
              user_id: newWallet.user_id,
              balance: newWallet.balance,
              created_at: newWallet.created_at,
              updated_at: newWallet.updated_at
            });
          }
        } else if (existingWallets && existingWallets.length > 0) {
          setWallet({
            user_id: existingWallets[0].user_id,
            balance: existingWallets[0].balance,
            created_at: existingWallets[0].created_at,
            updated_at: existingWallets[0].updated_at
          });
        }
      } catch (err) {
        logger.error('Error fetching wallet data:', err);
        setWalletError('Failed to load wallet data. Please try again or contact support.');
      } finally {
        setWalletLoading(false);
      }
    };

    fetchWalletData();

    // Set up real-time subscription for wallet changes
    const walletSubscription = supabase
      .channel('admin_wallet_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_wallets',
          filter: `user_id=eq.${ADMIN_USER_ID}`
        },
        (payload) => {
          if (payload.new) {
            type WalletData = {
              user_id: string;
              balance: number;
              created_at: string;
              updated_at: string;
            };
            const walletData = payload.new as WalletData;
            setWallet({
              user_id: walletData.user_id,
              balance: walletData.balance,
              created_at: walletData.created_at,
              updated_at: walletData.updated_at
            });
            // Clear any wallet errors since we have data
            setWalletError(null);
          }
        }
      )
      .subscribe();

    return () => {
      walletSubscription.unsubscribe();
    };
  }, [user, navigate]);

  // Fetch transactions with pagination and filtering
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!user || !wallet) return;

      try {
        setTransactionLoading(true);
        setTransactionError(null);
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        
        // Use wallet_transactions table for admin transactions
        const query = supabase
          .from('wallet_transactions')
          .select('id, user_id, type, amount, balance_after, description, status, created_at, completed_at, related_entity_id, related_entity_type', { count: 'exact' })
          .eq('user_id', ADMIN_USER_ID);
        
        // Apply type filter
        if (filterType !== 'all') {
          query.eq('type', filterType);
        }
        
        // Apply search if provided
        if (searchTerm) {
          query.ilike('description', `%${searchTerm}%`);
        }
        
        const { data, count, error } = await query
          .order('created_at', { ascending: false })
          .range(from, to);
          
        if (error) {
          logger.error('Error fetching transactions:', error);
          if (error.code === 'PGRST116') {
            // No data found, this is ok for a new admin wallet
            setTransactions([]);
            setTotalPages(1);
            return;
          }
          throw error;
        }
        
        // Calculate total pages
        if (count !== null) {
          setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
        }
        
        // Handle case with no data
        if (!data || data.length === 0) {
          setTransactions([]);
          return;
        }
        
        // Map transaction data to our expected format
        const enrichedTransactions = data.map((tx) => {
          return {
            id: tx.id,
            user_id: tx.user_id,
            type: tx.type as 'deposit' | 'withdrawal' | 'trade' | 'refund' | 'bonus' | 'service_fee',
            amount: tx.amount,
            balance_after: tx.balance_after,
            description: tx.description,
            status: tx.status as 'pending' | 'completed' | 'failed',
            created_at: tx.created_at,
            completed_at: tx.completed_at,
            related_entity_id: tx.related_entity_id || null,
            related_entity_type: tx.related_entity_type || null
          };
        });

        setTransactions(enrichedTransactions);
      } catch (err) {
        logger.error('Error fetching transactions:', err);
        setTransactionError('No transaction history available yet.');
        // Still set empty transactions so UI can render properly
        setTransactions([]);
      } finally {
        setTransactionLoading(false);
      }
    };

    fetchTransactions();
    
    // Set up real-time subscription for new transactions
    const txSubscription = supabase
      .channel('admin_transactions_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'wallet_transactions',
          filter: `user_id=eq.${ADMIN_USER_ID}`
        },
        () => {
          fetchTransactions(); // Reload transactions when a new one is added
        }
      )
      .subscribe();

    return () => {
      txSubscription.unsubscribe();
    };
  }, [user, wallet, page, pageSize, filterType, searchTerm]);

  // Fetch transaction totals - new effect
  useEffect(() => {
    const fetchTransactionTotals = async () => {
      if (!user || !wallet) return;

      try {
        setStatsLoading(true);
        
        // Fetch total service fees
        const { data: serviceFeeData, error: serviceFeeError } = await supabase
          .from('wallet_transactions')
          .select('amount')
          .eq('user_id', ADMIN_USER_ID)
          .eq('type', 'service_fee');
          
        if (serviceFeeError) throw serviceFeeError;
        
        // Fetch total deposits
        const { data: depositData, error: depositError } = await supabase
          .from('wallet_transactions')
          .select('amount')
          .eq('user_id', ADMIN_USER_ID)
          .eq('type', 'deposit');
          
        if (depositError) throw depositError;
        
        // Fetch total withdrawals
        const { data: withdrawalData, error: withdrawalError } = await supabase
          .from('wallet_transactions')
          .select('amount')
          .eq('user_id', ADMIN_USER_ID)
          .eq('type', 'withdrawal');
          
        if (withdrawalError) throw withdrawalError;
        
        // Fetch total transaction count
        const { count, error: countError } = await supabase
          .from('wallet_transactions')
          .select('id', { count: 'exact' })
          .eq('user_id', ADMIN_USER_ID);
          
        if (countError) throw countError;
        
        // Calculate sums
        const serviceFeeSum = serviceFeeData?.reduce((sum, tx) => sum + Number(tx.amount ?? 0), 0) || 0;
        const depositSum = depositData?.reduce((sum, tx) => sum + Number(tx.amount ?? 0), 0) || 0;
        const withdrawalSum = withdrawalData?.reduce((sum, tx) => sum + Number(tx.amount ?? 0), 0) || 0;

        let activityFees = 0;
        try {
          activityFees = await sumPlatformFeeAndSpreadFromBets();
        } catch (aggErr) {
          logger.error('Error aggregating platform fee & spread from user_bets:', aggErr);
        }

        setTotals({
          serviceFees: serviceFeeSum,
          platformFeeAndSpread: activityFees,
          deposits: depositSum,
          withdrawals: withdrawalSum,
          totalTransactions: count || 0
        });
      } catch (err) {
        logger.error('Error fetching transaction totals:', err);
      } finally {
        setStatsLoading(false);
      }
    };

    fetchTransactionTotals();
  }, [user, wallet]);

  const handleDeposit = async (amount: number) => {
    if (!user) return;
    
    try {
      setError(null);
      
      // Try using RPC first
      try {
        // Call the RPC function for deposit
        const { error } = await supabase.rpc('process_admin_deposit', {
          amount,
          description: `Manual deposit of ${formatCurrency(amount)}`
        });
        
        if (error) throw error;
        
        // Refresh wallet data (should happen via realtime subscription)
        if (!wallet) {
          // Fetch the wallet directly
          const { data: adminWallets, error: fetchError } = await supabase
            .from('user_wallets')
            .select('*')
            .eq('user_id', ADMIN_USER_ID)
            .limit(1);
            
          if (fetchError) {
            logger.error('Error fetching admin wallet:', fetchError);
            throw fetchError;
          }
          
          if (adminWallets && adminWallets.length > 0) {
            setWallet({
              user_id: adminWallets[0].user_id,
              balance: adminWallets[0].balance,
              created_at: adminWallets[0].created_at,
              updated_at: adminWallets[0].updated_at
            });
          }
        }
      } catch (rpcError) {
        logger.debug('RPC deposit failed, using direct DB operations:', {
          userId: ADMIN_USER_ID,
          amount,
          error: rpcError instanceof Error ? rpcError.message : 'Unknown error'
        });
        
        // Fallback to direct database operations if RPC fails
        // Use the admin wallet ID
        const walletUserId = ADMIN_USER_ID;
        
        // Get the current balance
        const { data: walletData, error: fetchError } = await supabase
          .from('user_wallets')
          .select('balance')
          .eq('user_id', ADMIN_USER_ID)
          .single();
          
        if (fetchError) {
          logger.error('Error fetching wallet data:', fetchError);
          throw fetchError;
        }
        
        const currentBalance = walletData?.balance || 0;
        const newBalance = currentBalance + amount;
        
        // Update wallet balance
        const { error: updateError } = await supabase
          .from('user_wallets')
          .update({ 
            balance: newBalance,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', ADMIN_USER_ID);
          
        if (updateError) {
          logger.error('Error updating wallet balance:', updateError);
          throw updateError;
        }
        
        // Create transaction record
        const { error: txError } = await supabase
          .from('wallet_transactions')
          .insert({
            user_id: walletUserId,
            type: 'deposit',
            amount: amount,
            balance_after: newBalance,
            description: `Manual deposit of ${formatCurrency(amount)}`,
            status: 'completed',
            completed_at: new Date().toISOString()
          });
          
        if (txError) {
          logger.error('Error recording transaction:', txError);
          throw txError;
        }
        
        // Update wallet state
        setWallet(prev => {
          if (!prev) return {
            user_id: ADMIN_USER_ID,
            balance: newBalance,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          };
          
          return {
            ...prev,
            balance: newBalance,
            updated_at: new Date().toISOString()
          };
        });
      }
      
      // Close modal
      setShowDepositModal(false);
    } catch (err) {
      logger.error('Error processing deposit:', err);
      setError('Failed to process deposit. Please try again.');
    }
  };

  const handleWithdraw = async (amount: number) => {
    if (!user || !wallet) return;
    
    try {
      setError(null);
      
      // Try using RPC first
      try {
        // Call the RPC function for withdrawal
        const { error } = await supabase.rpc('process_admin_withdrawal', {
          amount,
          description: `Manual withdrawal of ${formatCurrency(amount)}`
        });
        
        if (error) throw error;
        
        // Refresh wallet data (should happen via realtime subscription)
      } catch (rpcError) {
        logger.debug('RPC withdrawal failed, using direct DB operations:', {
          userId: ADMIN_USER_ID,
          amount,
          error: rpcError instanceof Error ? rpcError.message : 'Unknown error'
        });
        
        // Fallback to direct database operations if RPC fails
        const walletUserId = ADMIN_USER_ID;
        
        // Get the current balance
        const { data: walletData, error: fetchError } = await supabase
          .from('user_wallets')
          .select('balance')
          .eq('user_id', walletUserId)
          .single();
          
        if (fetchError) {
          logger.error('Error fetching wallet data:', fetchError);
          throw fetchError;
        }
        
        const currentBalance = walletData?.balance || 0;
        
        // Check if enough funds
        if (currentBalance < amount) {
          throw new Error('Insufficient funds in admin wallet');
        }
        
        const newBalance = currentBalance - amount;
        
        // Update wallet balance
        const { error: updateError } = await supabase
          .from('user_wallets')
          .update({ 
            balance: newBalance,
            updated_at: new Date().toISOString()
          })
          .eq('user_id', walletUserId);
          
        if (updateError) {
          logger.error('Error updating wallet balance:', updateError);
          throw updateError;
        }
        
        // Create transaction record
        const { error: txError } = await supabase
          .from('wallet_transactions')
          .insert({
            user_id: walletUserId,
            type: 'withdrawal',
            amount: amount,
            balance_after: newBalance,
            description: `Manual withdrawal of ${formatCurrency(amount)}`,
            status: 'completed',
            completed_at: new Date().toISOString()
          });
          
        if (txError) {
          logger.error('Error recording transaction:', txError);
          throw txError;
        }
        
        // Update wallet state
        setWallet(prev => {
          if (!prev) return null; // Should never happen
          
          return {
            ...prev,
            balance: newBalance,
            updated_at: new Date().toISOString()
          };
        });
      }
      
      // Close modal
      setShowWithdrawModal(false);
    } catch (err) {
      logger.error('Error processing withdrawal:', err);
      setError('Failed to process withdrawal. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowUpRight className="w-4 h-4 text-green-500" />;
      case 'withdrawal':
        return <ArrowDownRight className="w-4 h-4 text-red-500" />;
      case 'trade':
        return <Clock className="w-4 h-4 text-blue-500" />;
      case 'refund':
        return <Clock className="w-4 h-4 text-purple-500" />;
      case 'bonus':
        return <Clock className="w-4 h-4 text-pink-500" />;
      case 'service_fee':
        return <ArrowUpRight className="w-4 h-4 text-green-500" />;
      default:
        return <Wallet className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTransactionStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTransactionTypeStyle = (type: string) => {
    switch (type) {
      case 'deposit':
        return 'text-green-600';
      case 'service_fee':
        return 'text-green-600';
      case 'withdrawal':
        return 'text-red-600';
      case 'trade':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  // Add a function to handle transaction row clicks
  const handleTransactionClick = (transaction: AdminTransaction) => {
    setSelectedTransaction(transaction);
    setShowDetailsModal(true);
  };

  if (!user || user.app_metadata?.role !== 'admin') {
    return null;
  }

  return (
    <div className="py-4 sm:py-8 container mx-auto px-3 sm:px-4">
      <div className="flex items-center gap-3 mb-4 sm:mb-6">
        <Link to="/admin/dashboard" className="text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-6 h-6" />
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold">Wallet Management</h1>
      </div>
      
      <div className="grid grid-cols-1 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-0">
            <div className="flex items-center">
              <Wallet className="w-6 h-6 sm:w-8 sm:h-8 text-wayja-teal mr-3 sm:mr-4" />
              <div>
                <h2 className="text-base sm:text-lg font-medium">Current Balance</h2>
                {walletLoading ? (
                  <div className="flex items-center h-7 sm:h-9 mt-1">
                    <div className="animate-spin rounded-full h-4 sm:h-5 w-4 sm:w-5 border-b-2 border-wayja-teal mr-2"></div>
                    <span className="text-gray-500 text-sm sm:text-base">Loading wallet...</span>
                  </div>
                ) : wallet ? (
                  <p className="text-2xl sm:text-3xl font-bold">{formatCurrency(wallet.balance)}</p>
                ) : (
                  <p className="text-2xl sm:text-3xl font-bold text-gray-400">{formatCurrency(0)}</p>
                )}
              </div>
            </div>
            
            <div className="flex gap-2 sm:gap-3 self-end sm:self-auto">
              <button
                onClick={() => setShowDepositModal(true)}
                disabled={walletLoading}
                className="bg-green-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
              >
                <ArrowUpRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Deposit</span>
              </button>
              <button
                onClick={() => setShowWithdrawModal(true)}
                disabled={walletLoading || !wallet || wallet.balance <= 0}
                className="bg-red-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-1 sm:gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-xs sm:text-sm"
              >
                <ArrowDownRight className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Withdraw</span>
              </button>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium mb-3 sm:mb-4">Quick Stats</h2>
          {statsLoading ? (
            <div className="flex justify-center py-3 sm:py-4">
              <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-wayja-teal"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="bg-gray-50 p-2 sm:p-3 rounded-md">
                <p className="text-xs sm:text-sm text-gray-500">Withdrawals</p>
                <p className="text-base sm:text-xl font-semibold truncate">
                  {formatCurrency(totals.withdrawals)}
                </p>
              </div>
              <div className="bg-gray-50 p-2 sm:p-3 rounded-md">
                <p className="text-xs sm:text-sm text-gray-500">Deposits</p>
                <p className="text-base sm:text-xl font-semibold truncate">
                  {formatCurrency(totals.deposits)}
                </p>
              </div>
              <div className="bg-gray-50 p-2 sm:p-3 rounded-md col-span-2">
                <p className="text-xs sm:text-sm text-gray-500">Platform fee &amp; spread</p>
                <p className="text-base sm:text-xl font-semibold truncate">
                  {formatCurrency(totals.platformFeeAndSpread)}
                </p>
                <p className="text-[11px] text-gray-500 mt-1 leading-snug">
                  Activity on bets: pool markets use service fee on stakes; LMSR uses gross spread revenue (matches portfolio logic).
                  Admin wallet service_fee credits (cash movement){' '}
                  {formatCurrency(totals.serviceFees)} — includes net LMSR spread after affiliate and creator shares.
                </p>
              </div>
              <div className="bg-gray-50 p-2 sm:p-3 rounded-md col-span-2">
                <p className="text-xs sm:text-sm text-gray-500">Total Transactions</p>
                <p className="text-base sm:text-xl font-semibold truncate">{totals.totalTransactions}</p>
              </div>
            </div>
          )}
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-medium mb-3 sm:mb-4">Market Funding</h2>
          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">
            Fund specific markets to incentivize user participation and increase market liquidity.
          </p>
          <Link
            to="/admin/markets"
            className="inline-block px-3 sm:px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors text-xs sm:text-sm"
          >
            View Markets
          </Link>
        </div>
      </div>
      
      {/* Transaction History */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-4 sm:mb-6 gap-3">
          <h2 className="text-lg sm:text-xl font-medium">Transaction History</h2>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal w-full sm:w-64 text-sm"
              />
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            </div>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal text-sm w-full sm:w-auto"
            >
              <option value="all">All Transactions</option>
              <option value="service_fee">Service Fees</option>
              <option value="deposit">Admin Deposits</option>
              <option value="withdrawal">Admin Withdrawals</option>
            </select>
          </div>
        </div>
        
        {walletError && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {walletError}
          </div>
        )}
        
        {transactionError && (
          <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-700 rounded text-sm">
            {transactionError}
          </div>
        )}
        
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
            {error}
          </div>
        )}
        
        {transactionLoading && transactions.length === 0 ? (
          <div className="py-6 sm:py-8 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-wayja-teal"></div>
          </div>
        ) : transactions.length === 0 ? (
          <div className="py-6 sm:py-8 text-center text-gray-500 text-sm sm:text-base">
            No transactions found. Use the deposit button to add funds.
          </div>
        ) : (
          <>
            {/* Mobile Transaction List */}
            <div className="block sm:hidden">
              <div className="space-y-3">
                {transactions.map((transaction) => (
                  <div 
                    key={transaction.id} 
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50"
                    onClick={() => handleTransactionClick(transaction)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        {getTransactionIcon(transaction.type)}
                        <span className="ml-2 text-sm font-medium">
                          {getTransactionLabel(transaction.type, transaction.description)}
                        </span>
                      </div>
                      <span className={`text-sm font-medium ${getTransactionTypeStyle(transaction.type)}`}>
                        {transaction.type === 'withdrawal' ? '-' : '+'}{formatCurrency(transaction.amount)}
                      </span>
                    </div>
                    
                    <p className="text-xs text-gray-500 mb-2 truncate">
                      {transaction.description || 'No description'}
                    </p>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">
                        {formatDate(transaction.created_at).split(',')[0]}
                      </span>
                      <span className={`px-2 py-0.5 text-xs leading-5 font-semibold rounded-full ${getTransactionStatusColor(transaction.status)}`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Desktop Transaction Table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance After</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Related To</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((transaction) => (
                    <tr 
                      key={transaction.id} 
                      className="hover:bg-gray-50 cursor-pointer" 
                      onClick={() => handleTransactionClick(transaction)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getTransactionIcon(transaction.type)}
                          <span className="ml-2">
                            {getTransactionLabel(transaction.type, transaction.description)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`font-medium ${getTransactionTypeStyle(transaction.type)}`}>
                          {transaction.type === 'withdrawal' ? '-' : '+'}{formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {formatCurrency(transaction.balance_after)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs">
                          {transaction.description || '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getTransactionStatusColor(transaction.status)}`}>
                          {transaction.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(transaction.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <RelatedEntitySummary transaction={transaction} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 flex justify-center">
                <nav className="flex items-center space-x-1">
                  {/* Previous Button */}
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 rounded-md bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  {generatePageNumbers(page, totalPages).map((pageNumber: number | string, index: number) => (
                    <React.Fragment key={index}>
                      {typeof pageNumber === 'number' ? (
                        <button
                          onClick={() => setPage(pageNumber)}
                          className={`px-3 py-1 rounded-md border text-sm font-medium ${
                            page === pageNumber
                              ? 'bg-wayja-teal border-wayja-teal text-white z-10'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {pageNumber}
                        </button>
                      ) : (
                        <span className="px-3 py-1 text-sm text-gray-500">...</span>
                      )}
                    </React.Fragment>
                  ))}

                  {/* Next Button */}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 rounded-md bg-white border border-gray-300 text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* Transaction Details Modal */}
      {showDetailsModal && selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedTransaction(null);
          }}
        />
      )}
      
      {/* Deposit Modal */}
      {showDepositModal && (
        <DepositModal
          onClose={() => setShowDepositModal(false)}
          onDeposit={handleDeposit}
        />
      )}
      
      {/* Withdraw Modal */}
      {showWithdrawModal && wallet && (
        <WithdrawModal
          onClose={() => setShowWithdrawModal(false)}
          onWithdraw={handleWithdraw}
          maxAmount={wallet.balance}
        />
      )}
    </div>
  );
}

interface DepositModalProps {
  onClose: () => void;
  onDeposit: (amount: number) => Promise<void>;
}

function DepositModal({ onClose, onDeposit }: DepositModalProps) {
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate amount
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    try {
      await onDeposit(numAmount);
    } catch (err) {
      logger.error('Error processing deposit:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to process deposit';
      setError(errorMessage || 'Failed to process deposit. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-3 sm:mx-0">
        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-medium">Deposit to Admin Wallet</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-3 sm:p-4">
          {error && (
            <div className="mb-4 p-2 sm:p-3 bg-red-100 border border-red-400 text-red-700 rounded text-xs sm:text-sm">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="amount" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Amount (ZAR)
            </label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal text-sm"
              required
            />
          </div>
          
          <div className="flex justify-end gap-2 sm:gap-3 mt-5 sm:mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-xs sm:text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              Deposit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface WithdrawModalProps {
  onClose: () => void;
  onWithdraw: (amount: number) => Promise<void>;
  maxAmount: number;
}

function WithdrawModal({ onClose, onWithdraw, maxAmount }: WithdrawModalProps) {
  const [amount, setAmount] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { formatCurrency } = useSettings();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate amount
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    
    if (numAmount > maxAmount) {
      setError(`You cannot withdraw more than ${formatCurrency(maxAmount)}`);
      return;
    }
    
    try {
      await onWithdraw(numAmount);
    } catch (err) {
      logger.error('Error processing withdrawal:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to process withdrawal';
      setError(errorMessage.includes('Insufficient funds') ? 
        'Insufficient funds in admin wallet' : 
        'Failed to process withdrawal. Please try again.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-3 sm:mx-0">
        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-medium">Withdraw from Admin Wallet</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-3 sm:p-4">
          {error && (
            <div className="mb-4 p-2 sm:p-3 bg-red-100 border border-red-400 text-red-700 rounded text-xs sm:text-sm">
              {error}
            </div>
          )}
          
          <div className="mb-4">
            <label htmlFor="amount" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Amount (ZAR)
            </label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              step="0.01"
              min="0.01"
              max={maxAmount}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wayja-teal text-sm"
              required
            />
            <p className="text-xs sm:text-sm text-gray-500 mt-1">
              Available: R{maxAmount.toFixed(2)}
            </p>
          </div>
          
          <div className="flex justify-end gap-2 sm:gap-3 mt-5 sm:mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-3 sm:px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-xs sm:text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm"
            >
              Withdraw
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Add a new TransactionDetailsModal component
interface TransactionDetailsModalProps {
  transaction: AdminTransaction;
  onClose: () => void;
}

function TransactionDetailsModal({ transaction, onClose }: TransactionDetailsModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [betDetails, setBetDetails] = useState<{
    user_id?: string;
    user_email?: string;
    user_name?: string;
    market_title?: string;
    market_id?: string;
    outcome_name?: string;
    amount?: number;
    service_fee_amount?: number;
    spread_revenue?: number | null;
    market_engine_type?: string | null;
    created_at?: string;
    bet_id?: string;
  } | null>(null);
  const { formatCurrency } = useSettings();
  
  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  useEffect(() => {
    const fetchTransactionDetails = async () => {
      if (transaction.type === 'service_fee' && transaction.related_entity_id) {
        try {
          setLoading(true);
          setError(null);

          const betId = transaction.related_entity_id;
          let extras: {
            spread_revenue?: number | null;
            market_id?: string;
            market_engine_type?: string | null;
          } = {};

          try {
            const { data: row } = await supabase
              .from('user_bets')
              .select('spread_revenue, market_id, markets(engine_type)')
              .eq('id', betId)
              .maybeSingle();
            if (row) {
              const mk = row.markets as { engine_type?: string } | null;
              extras = {
                spread_revenue: row.spread_revenue != null ? Number(row.spread_revenue) : null,
                market_id: row.market_id ?? undefined,
                market_engine_type: mk?.engine_type ?? null,
              };
            }
          } catch {
            // Non-fatal — RPC may still return core fields
          }

          try {
            const { data } = await supabase.rpc('get_bet_and_user_details', { p_bet_id: betId });

            if (data && !data.error) {
              setBetDetails({
                bet_id: data.bet.id,
                user_id: data.bet.user_id,
                user_email: data.user?.email,
                user_name: data.user?.full_name,
                market_title: data.market?.title || 'Unknown market',
                market_id: data.market?.id ?? extras.market_id,
                outcome_name: data.bet.outcome_name,
                amount: data.bet.amount,
                service_fee_amount: transaction.amount,
                spread_revenue: extras.spread_revenue ?? null,
                market_engine_type: extras.market_engine_type ?? null,
                created_at: data.bet.created_at,
              });
              return;
            }
          } catch {
            // fallback below
          }

          setBetDetails({
            bet_id: betId,
            service_fee_amount: transaction.amount,
            created_at: transaction.created_at,
            market_id: extras.market_id,
            spread_revenue: extras.spread_revenue ?? null,
            market_engine_type: extras.market_engine_type ?? null,
          });
        } catch (err) {
          logger.error('Error loading transaction details:', err);
          setError('Failed to load details');
        } finally {
          setLoading(false);
        }
      } else {
        setBetDetails({
          created_at: transaction.created_at,
          service_fee_amount: transaction.amount,
        });
      }
    };

    fetchTransactionDetails();
  }, [transaction]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full mx-3 sm:mx-0">
        <div className="flex justify-between items-center p-3 sm:p-4 border-b border-gray-200">
          <h2 className="text-base sm:text-lg font-medium">Transaction Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center p-6">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-wayja-teal"></div>
          </div>
        ) : (
          <div className="p-3 sm:p-4">
            {error && (
              <div className="mb-4 p-2 sm:p-3 bg-red-100 text-red-700 rounded-md text-xs sm:text-sm">
                {error}
              </div>
            )}
            
            <div className="space-y-3 sm:space-y-4">
              {/* Transaction Info */}
              <div className="bg-gray-50 p-3 rounded-md">
                <h3 className="font-medium mb-2 text-sm sm:text-base">Transaction</h3>
                <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                  <div>
                    <p className="text-gray-500">Type</p>
                    <p>{getTransactionLabel(transaction.type, transaction.description)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Amount</p>
                    <p className="font-medium">{formatCurrency(transaction.amount)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Date</p>
                    <p>{formatDate(transaction.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status</p>
                    <p className="capitalize">{transaction.status}</p>
                  </div>
                  {transaction.description && (
                    <div className="col-span-2">
                      <p className="text-gray-500">Description</p>
                      <p className="break-words">{transaction.description}</p>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Bet Details (only for service fees) */}
              {transaction.type === 'service_fee' && betDetails && (
                <div className="bg-gray-50 p-3 rounded-md">
                  <h3 className="font-medium mb-2 text-sm sm:text-base">Related Trade</h3>
                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    {betDetails.market_title && betDetails.market_id ? (
                      <div className="col-span-2">
                        <p className="text-gray-500">Market</p>
                        <Link
                          to={`/admin/market/${betDetails.market_id}`}
                          className="text-wayja-teal hover:underline font-medium break-words"
                          onClick={onClose}
                        >
                          {betDetails.market_title}
                        </Link>
                      </div>
                    ) : betDetails.market_title ? (
                      <div className="col-span-2">
                        <p className="text-gray-500">Market</p>
                        <p>{betDetails.market_title}</p>
                      </div>
                    ) : null}
                    {betDetails.outcome_name && (
                      <div>
                        <p className="text-gray-500">Outcome</p>
                        <p>{betDetails.outcome_name}</p>
                      </div>
                    )}
                    {betDetails.amount != null && betDetails.amount > 0 && (
                      <div>
                        <p className="text-gray-500">Trade Amount</p>
                        <p>{formatCurrency(betDetails.amount)}</p>
                      </div>
                    )}
                    {betDetails.spread_revenue != null && Number(betDetails.spread_revenue) > 0 && (
                      <div className="col-span-2">
                        <p className="text-gray-500">LMSR spread revenue (bet)</p>
                        <p className="font-medium">{formatCurrency(Number(betDetails.spread_revenue))}</p>
                      </div>
                    )}
                    {(betDetails.user_name || betDetails.user_email) && (
                      <div className="col-span-2">
                        <p className="text-gray-500">User</p>
                        <p>{betDetails.user_name || betDetails.user_email || 'Unknown user'}</p>
                      </div>
                    )}
                    {betDetails.created_at && (
                      <div className="col-span-2">
                        <p className="text-gray-500">Trade Placed</p>
                        <p>{formatDate(betDetails.created_at)}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={onClose}
                className="px-3 sm:px-4 py-2 bg-wayja-teal text-white rounded-md hover:bg-opacity-90 transition-colors text-xs sm:text-sm"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 